import { useEffect } from 'react'
import { Plus, Trash2, ArrowRight, Volume2 } from 'lucide-react'
import type { ExerciseEditorProps, MatchingPairData } from '@/types'
import AudioPlayer from '@/components/common/AudioPlayer'
import { encodeMatchingPairs } from '@/lib/exerciseUtils'

interface Props extends ExerciseEditorProps {
  matchingPairs: MatchingPairData[]
  setMatchingPairs: React.Dispatch<React.SetStateAction<MatchingPairData[]>>
  skill: string
}

export default function MatchingEditor({ formData, setFormData, isViewMode, matchingPairs, setMatchingPairs, skill }: Props) {
  const isAudioMode = skill === 'listening'

  // Sincronizar pairs -> formData.options al cambiar
  useEffect(() => {
    const encoded = encodeMatchingPairs(matchingPairs.map(p => ({ left: p.left, right: p.right })))
    setFormData(prev => ({
      ...prev,
      options: encoded,
      correct_answer: JSON.stringify(encoded)
    }))
  }, [matchingPairs])

  const addPair = () => {
    if (matchingPairs.length >= 8) return
    setMatchingPairs(prev => [
      ...prev,
      { id: Date.now().toString(), left: '', right: '' }
    ])
  }

  const removePair = (id: string) => {
    if (matchingPairs.length <= 2) return
    setMatchingPairs(prev => prev.filter(p => p.id !== id))
  }

  const updatePair = (id: string, field: 'left' | 'right', value: string) => {
    setMatchingPairs(prev =>
      prev.map(p => p.id === id ? { ...p, [field]: value } : p)
    )
  }

  const isValidUrl = (url: string) => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  return (
    <div className="space-y-4">
      {/* Instrucciones */}
      <div className={`p-3 border rounded-lg ${isAudioMode ? 'bg-[#0055A4]/5 border-[#0055A4]/20' : 'bg-purple-50 border-purple-200'}`}>
        <p className={`text-sm ${isAudioMode ? 'text-[#003d7a]' : 'text-purple-800'}`}>
          {isAudioMode ? (
            <>
              Modo <strong>Audio</strong>: La columna izquierda contiene URLs de audio.
              El estudiante escuchará cada audio y lo conectará con el texto correcto de la columna derecha.
            </>
          ) : (
            <>
              Crea las parejas que el estudiante deberá conectar. Columna A se muestra a la izquierda
              y Columna B a la derecha (desordenada).
            </>
          )}
        </p>
      </div>

      {/* Pregunta/Instruccion */}
      <div>
        <label htmlFor="matching-instruction" className="block text-sm font-medium text-gray-700 mb-1">
          Instruccion del ejercicio
        </label>
        <input id="matching-instruction" type="text" value={formData.question}
          onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
          placeholder={isAudioMode
            ? "Escucha cada audio y conectalo con el texto correcto..."
            : "Conecta cada palabra con su significado..."
          }
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
          required
          disabled={isViewMode}
        />
      </div>

      {/* Header de columnas */}
      <div className="grid grid-cols-[1fr_32px_1fr_40px] gap-2 px-1">
        <div className={`text-sm font-semibold px-3 py-2 rounded-lg text-center ${
          isAudioMode
            ? 'text-[#0055A4] bg-[#0055A4]/5'
            : 'text-purple-700 bg-purple-50'
        }`}>
          {isAudioMode ? (
            <span className="flex items-center justify-center gap-1">
              <Volume2 className="w-4 h-4" />
              Audio
            </span>
          ) : 'Columna A'}
        </div>
        <div />
        <div className="text-sm font-semibold text-indigo-700 bg-indigo-50 px-3 py-2 rounded-lg text-center">
          {isAudioMode ? 'Texto' : 'Columna B'}
        </div>
        <div />
      </div>

      {/* Parejas */}
      <div className="space-y-3">
        {matchingPairs.map((pair, index) => (
          <div key={pair.id} className="space-y-1">
            <div className="grid grid-cols-[1fr_32px_1fr_40px] gap-2 items-center">
              <div className="relative">
                <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium ${
                  isAudioMode ? 'text-[#0055A4]' : 'text-purple-500'
                }`}>
                  {index + 1}.
                </span>
                <input
                  type={isAudioMode ? 'url' : 'text'}
                  value={pair.left}
                  onChange={(e) => updatePair(pair.id, 'left', e.target.value)}
                  placeholder={isAudioMode ? "https://...audio.mp3" : "Elemento izquierdo..."}
                  className={`w-full pl-8 pr-3 py-2.5 border-2 rounded-lg focus:ring-2 bg-purple-50/30 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    isAudioMode
                      ? 'border-[#0055A4]/20 focus:ring-[#0055A4] focus:border-[#0055A4] bg-[#0055A4]/10'
                      : 'border-purple-200 focus:ring-purple-400 focus:border-purple-400 bg-purple-50/30'
                  }`}
                  disabled={isViewMode}
                />
              </div>
              <div className="flex justify-center">
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={pair.right}
                onChange={(e) => updatePair(pair.id, 'right', e.target.value)}
                placeholder={isAudioMode ? "Texto / significado..." : "Elemento derecho..."}
                className="w-full px-3 py-2.5 border-2 border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 bg-indigo-50/30 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
              {matchingPairs.length > 2 && !isViewMode && (
                <button
                  type="button"
                  onClick={() => removePair(pair.id)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              {(matchingPairs.length <= 2 || isViewMode) && <div />}
            </div>

            {/* Preview de audio debajo del input URL */}
            {isAudioMode && pair.left && isValidUrl(pair.left) && (
              <div className="ml-8 mr-[72px]">
                <AudioPlayer src={pair.left} compact className="w-full" />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Agregar par */}
      {!isViewMode && matchingPairs.length < 8 && (
        <button
          type="button"
          onClick={addPair}
          className={`flex items-center gap-2 text-sm font-medium ${
            isAudioMode
              ? 'text-[#0055A4] hover:text-[#0055A4]'
              : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          <Plus className="w-4 h-4" />
          Agregar pareja ({matchingPairs.length}/8)
        </button>
      )}
    </div>
  )
}
